// The details pane for one file: how to open it, what it weighs, every version it has had, who can see
// it, and what has been done to it. The version list is the reason this is a pane and not a tooltip —
// "which copy is current and what changed" is the question a document store exists to answer.

import React, { useState } from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { formatBytes, formatDateTime } from '/imports/utils';
import { FileMeta } from '/imports/types';
import { css } from '/imports/ui/css';
import { Blade } from '/imports/ui/Blade';
import { Button } from '/imports/ui/Button';
import { Icon } from '/imports/ui/Icon';
import { FileInput } from '/imports/ui/FileInput';
import { FileViewerModal, fileIsViewable } from '/imports/ui/FileViewerModal';
import { toastOnError } from '/imports/ui/utils';
import { useMethod } from '/imports/hooks';
import {
  DriveVersionRow, driveAddFileVersion, driveFileDetail, driveFileUrls, driveGallery, driveIsArchive,
  driveIsEditable, driveIsSheet, driveReindexArchive, driveRequestOcr, driveRevertVersion,
} from '/imports/api/drive';
import { DriveGalleryModal } from '/imports/ui/DriveGalleryModal';
import { DriveSheetModal } from '/imports/ui/DriveSheetModal';
import { DriveTextEditorModal } from '/imports/ui/DriveTextEditorModal';
import { DriveName, driveAccessLabel } from '/imports/ui/DriveShared';
import { DrivePermissions } from '/imports/ui/DrivePermissions';

const DriveDetailsBladeStyled = styled(Blade)`
  .blade-body { padding: 3.5rem 1.5rem 3rem 1.5rem; overflow-y: auto; height: 100%; }
  h2 { font-size: 1rem; margin: 0 0 0.2rem; display: flex; align-items: center; gap: 0.4rem; }
  h2 .cell-name { min-width: 0; }
  .path { font-size: 0.78rem; color: ${css.grey4}; margin-bottom: 0.75rem; word-break: break-all; }
  section { margin-bottom: 1.5rem; }
  section > h3 {
    font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; color: ${css.grey4};
    margin: 0 0 0.5rem; padding-bottom: 0.2rem; border-bottom: 1px solid ${css.grey7};
  }
  .actions { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1.25rem; }
  .facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr)); gap: 0.4rem 1rem; }
  .fact .k { color: ${css.grey4}; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.04em; }
  .fact .v { font-size: 0.88rem; font-variant-numeric: tabular-nums; }
  .version { display: flex; align-items: baseline; gap: 0.5rem; padding: 0.4rem 0; border-bottom: 1px solid ${css.grey8}; font-size: 0.85rem; }
  .version .n { font-weight: 600; font-variant-numeric: tabular-nums; min-width: 2.2rem; }
  .version .body { flex: 1; min-width: 0; }
  .version .note { color: ${css.grey2}; }
  .version .who { color: ${css.grey4}; font-size: 0.76rem; }
  .version .tools { display: flex; gap: 0.2rem; }
  .event { display: flex; gap: 0.5rem; padding: 0.3rem 0; font-size: 0.82rem; border-bottom: 1px solid ${css.grey8}; }
  .event .when { color: ${css.grey4}; white-space: nowrap; font-size: 0.76rem; }
  .duplicates { font-size: 0.8rem; }
  .duplicates li { margin-bottom: 0.15rem; word-break: break-all; }
`;

export interface DriveDetailsBladeProps {
  fileId: string;
  reload: number;
  onClose(): void;
  onChanged(): void;
}

export const DriveDetailsBlade = (props: DriveDetailsBladeProps) => {
  const [detail, loading] = useMethod(driveFileDetail, { _id: props.fileId }, [props.fileId, props.reload]);
  const [viewing, setViewing] = useState<FileMeta>();
  const [sheeting, setSheeting] = useState(false);
  const [gallery, setGallery] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const file = detail?.file;
  const canEdit = file?.access === 'editor' || file?.access === 'manager';
  const sheet = driveIsSheet(file?.ext);
  const archive = driveIsArchive(file?.ext);
  const editable = driveIsEditable(file?.ext);

  /** A minted url stands in for a stored FileMeta: it carries its own expiring token in the query. */
  const openFile = toastOnError(async (versionId?: string) => {
    if (!file) return;
    const urls = await driveFileUrls.call({ ids: [file._id], versionId, download: !fileIsViewable(file) });
    const url = urls[file._id];
    if (!url) return;
    const meta = { url, name: file.name, filename: file.name, type: file.type || '', size: file.size || 0, md5: '', token: '' };
    if (fileIsViewable(file)) setViewing(meta);
    else window.open(url, '_blank', 'noreferrer');
  });

  /** The PDF made from a document no browser renders, which is what "view" means for a .docx. */
  const openRendition = toastOnError(async () => {
    if (!file) return;
    const urls = await driveFileUrls.call({ ids: [file._id], rendition: 'pdf' });
    const url = urls[file._id];
    if (!url) return;
    setViewing({ url, name: `${file.name}.pdf`, filename: `${file.name}.pdf`, type: 'application/pdf', size: 0, md5: '', token: '' });
  });

  const download = toastOnError(async (versionId?: string) => {
    if (!file) return;
    const urls = await driveFileUrls.call({ ids: [file._id], versionId, download: true });
    if (urls[file._id]) window.open(urls[file._id], '_blank', 'noreferrer');
  });

  const addVersion = toastOnError(async (uploaded: FileMeta) => {
    setBusy(true);
    try {
      await driveAddFileVersion.call({ driveFileId: props.fileId, file: uploaded });
      props.onChanged();
    }
    finally {
      setBusy(false);
    }
  });

  const requestOcr = toastOnError(async () => {
    setBusy(true);
    try {
      await driveRequestOcr.call({ _id: props.fileId });
      props.onChanged();
    }
    finally {
      setBusy(false);
    }
  });

  const reindex = toastOnError(async () => {
    setBusy(true);
    try {
      await driveReindexArchive.call({ _id: props.fileId });
      props.onChanged();
    }
    finally {
      setBusy(false);
    }
  });

  const revert = toastOnError(async (version: DriveVersionRow) => {
    setBusy(true);
    try {
      await driveRevertVersion.call({ driveFileId: props.fileId, versionId: version._id });
      props.onChanged();
    }
    finally {
      setBusy(false);
    }
  });

  return (
    <DriveDetailsBladeStyled open onClose={props.onClose}>
      {loading && !detail && <div className="muted">{strings.Loading()}</div>}
      {file && (
        <>
          <h2><DriveName name={file.name} ext={file.ext} /></h2>
          <div className="path">{detail?.crumbs.map((crumb) => crumb.name).join(' / ')}</div>
          <div className="actions">
            {/* What "view" means depends on the file: a table for a spreadsheet, the converted PDF for a
                document, the file itself for anything a browser already renders. */}
            {sheet && <Button onClick={() => setSheeting(true)}>{strings.View()}</Button>}
            {archive && <Button onClick={() => setGallery(true)}>{strings.ViewGallery()}</Button>}
            {!sheet && !archive && fileIsViewable(file) && <Button onClick={() => openFile()}>{strings.View()}</Button>}
            {!sheet && !archive && !fileIsViewable(file) && file.hasPdfRendition && (
              <Button onClick={() => openRendition()}>{strings.DriveViewAsPdf()}</Button>
            )}
            {!sheet && !archive && !fileIsViewable(file) && !file.hasPdfRendition && (
              <Button onClick={() => openFile()}>{strings.Open()}</Button>
            )}
            {editable && <Button className="secondary" onClick={() => setEditing(true)}>{canEdit ? strings.Edit() : strings.View()}</Button>}
            <Button className="secondary" onClick={() => download()}>{strings.Download()}</Button>
            {/* A photo or a scan is never read automatically — 23,468 site photos would be days of OCR
                nobody asked for — so the offer is made here, per file, to whoever may change it. */}
            {/* Offered rather than automatic: rebuilding costs a thumbnail per picture, and the only
                reasons to want it are a failed run and a change to the width they are drawn at. */}
            {archive && canEdit && (
              <Button className="secondary" disabled={busy} onClick={() => reindex()}>{strings.ReindexGallery()}</Button>
            )}
            {canEdit && (file.textStatus === 'skipped' || file.textStatus === 'failed') && (
              <Button className="secondary" disabled={busy} tip={strings.ReadWithOcrHint()} onClick={() => requestOcr()}>
                {strings.ReadWithOcr()}
              </Button>
            )}
          </div>
          {file.textStatus === 'pending' && <div className="muted">{strings.TextPending()}</div>}
          {!sheet && !fileIsViewable(file) && !file.hasPdfRendition && file.renditionPending && (
            <div className="muted">{strings.DriveConverting()}</div>
          )}

          <section>
            <h3>{strings.Details()}</h3>
            <div className="facts">
              <div className="fact"><div className="k">{strings.Size()}</div><div className="v">{formatBytes(file.size || 0)}</div></div>
              <div className="fact"><div className="k">{strings.Type()}</div><div className="v">{file.type || '—'}</div></div>
              <div className="fact"><div className="k">{strings.Versions()}</div><div className="v">{file.versionCount || 1}</div></div>
              <div className="fact"><div className="k">{strings.Access()}</div><div className="v">{driveAccessLabel(file.access)}</div></div>
              <div className="fact"><div className="k">{strings.Updated()}</div><div className="v">{formatDateTime(file.updatedAt)}</div></div>
              <div className="fact"><div className="k">{strings.By()}</div><div className="v">{file.updatedByName || '—'}</div></div>
            </div>
          </section>

          {!!detail?.duplicates?.length && (
            <section>
              <h3>{strings.DriveDuplicateElsewhere()}</h3>
              <ul className="duplicates">
                {detail.duplicates.map((duplicate) => <li key={duplicate._id}>{duplicate.path}/{duplicate.name}</li>)}
              </ul>
            </section>
          )}

          <section>
            <h3>{strings.VersionHistory()}</h3>
            {detail?.versions.map((version) => (
              <div className="version" key={version._id}>
                <span className="n">v{version.versionNumber}</span>
                <span className="body">
                  <div className="note">{version.changeNote || (version.status === 'current' ? strings.CurrentVersion() : version.name)}</div>
                  <div className="who">
                    {formatDateTime(version.createdAt)}{version.createdByName ? ` · ${version.createdByName}` : ''} · {formatBytes(version.size)}
                  </div>
                </span>
                <span className="tools">
                  <button type="button" className="icon-btn" aria-label={strings.Download()} onClick={() => download(version._id)}>
                    <Icon icon="download" size={16} fill={css.grey3} />
                  </button>
                  {canEdit && version.status !== 'current' && (
                    <button type="button" className="icon-btn" aria-label={strings.RestoreItem()} disabled={busy} onClick={() => revert(version)}>
                      <Icon icon="switchAccess" size={16} fill={css.grey3} />
                    </button>
                  )}
                </span>
              </div>
            ))}
            {canEdit && (
              <FileInput
                label={strings.UploadNewVersion()}
                disabled={busy}
                onUploadDone={(uploaded) => addVersion(uploaded)}
              />
            )}
          </section>

          <section>
            <h3>{strings.Permissions()}</h3>
            <DrivePermissions
              kind="file"
              _id={file._id}
              acl={detail?.acl || []}
              ownAcl={detail?.ownAcl}
              canManage={file.access === 'manager'}
              onChanged={props.onChanged}
            />
          </section>

          <section>
            <h3>{strings.Activity()}</h3>
            {detail?.events.map((event) => (
              <div className="event" key={event._id}>
                <span className="when">{formatDateTime(event.at)}</span>
                <span>{event.action}{event.byName ? ` · ${event.byName}` : ''}</span>
              </div>
            ))}
            {!detail?.events.length && <div className="muted">{strings.NoActivity()}</div>}
          </section>
        </>
      )}
      {viewing && <FileViewerModal file={viewing} downloadName={file?.name} onClose={() => setViewing(undefined)} />}
      {sheeting && file && <DriveSheetModal _id={file._id} name={file.name} onClose={() => setSheeting(false)} />}
      {gallery && file && (
        <DriveGalleryModal
          title={file.name}
          load={(from, limit) => driveGallery.call({ _id: file._id, from, limit })}
          onClose={() => setGallery(false)}
        />
      )}
      {editing && file && (
        <DriveTextEditorModal _id={file._id} name={file.name} onClose={() => setEditing(false)} onSaved={props.onChanged} />
      )}
    </DriveDetailsBladeStyled>
  );
};
